import { useMemo, useState, type ReactNode } from 'react';
import type { PersonId } from '@shared/brand';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { cssVars, cx } from '../../design-system';
import { formatDateRange } from '../../format';
import { buildMonthGrid, WEEKDAY_LABELS } from './monthGrid';
import styles from './CalendarScreen.module.css';

/**
 * The month as a wall calendar — the other way to read the same absences.
 *
 * The person-by-day timeline answers «when is *this* person away»; a grid
 * answers «who is away on *this* day», which is the question asked while
 * choosing dates. Neither replaces the other, so the screen offers both rather
 * than picking.
 *
 * A day names the people, not a count. «3 fuera» tells a leader nothing they can
 * act on: whether Tuesday is covered depends on *which* three, and the sub-area
 * colour beside each name is what answers it at a glance.
 */

export interface DayOccupant {
  readonly key: string;
  readonly personId: PersonId;
  readonly name: string;
  readonly color: string;
  readonly title: string;
  /** Still awaiting a decision, so it must not read as settled. */
  readonly tentative: boolean;
}

interface Props {
  readonly anchor: CalendarDate;
  readonly today: CalendarDate;
  readonly absences: readonly VacationRequest[];
  readonly holidays: ReadonlySet<string>;
  readonly visiblePersonIds: ReadonlySet<string>;
  readonly colorFor: (personId: PersonId) => string;
  readonly nameFor: (personId: PersonId) => string;
}

/** Names shown before the cell folds the rest into «+N». Four rows is where a
    six-week month stops fitting on one screen. */
const MAX_NAMES = 3;

/** «Mariana Elizabeth Zambrano Robledo» in a 120px cell is a smear; the first
    given name and the initial of the first surname are what people scan for. */
const shortName = (full: string): string => {
  const parts = full.trim().split(/\s+/);
  const [first, second] = parts;
  if (first === undefined) return full;
  if (second === undefined) return first;
  return `${first} ${second.charAt(0)}.`;
};

export const MonthGridView = ({
  anchor,
  today,
  absences,
  holidays,
  visiblePersonIds,
  colorFor,
  nameFor,
}: Props): ReactNode => {
  /** El día cuya lista está desplegada, o `null`. Uno a la vez: dos celdas
   *  abiertas en una rejilla de treinta y cinco no se leen mejor, se leen peor. */
  const [expanded, setExpanded] = useState<string | null>(null);

  const weeks = useMemo(() => buildMonthGrid(anchor), [anchor]);

  /**
   * One pass over the absences rather than a scan per cell: a six-week grid is
   * 42 cells, and at 500 people the difference between 42 scans and one is the
   * difference between a smooth month change and a visible stall.
   */
  const occupantsByDay = useMemo(() => {
    const map = new Map<string, DayOccupant[]>();

    for (const absence of absences) {
      if (!visiblePersonIds.has(absence.personId)) continue;

      const tentative = absence.status !== 'Aprobada' && absence.status !== 'Tomada';
      const title = `${absence.folio} · ${absence.status} · ${formatDateRange(absence.range)}`;

      for (const day of absence.range.toDayArray()) {
        const iso = day.toISO();
        const entry: DayOccupant = {
          key: `${absence.id}_${iso}`,
          personId: absence.personId,
          name: nameFor(absence.personId),
          color: colorFor(absence.personId),
          title,
          tentative,
        };
        map.set(iso, [...(map.get(iso) ?? []), entry]);
      }
    }

    return map;
  }, [absences, visiblePersonIds, colorFor, nameFor]);

  return (
    <div className={styles.monthGrid}>
      <div className={styles.weekdayRow} aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={styles.weekdayLabel}>
            {label}
          </div>
        ))}
      </div>

      {weeks.map((week) => (
        <div key={week.key} className={styles.weekRow}>
          {week.days.map((day) => {
            const iso = day.date.toISO();
            const occupants = occupantsByDay.get(iso) ?? [];
            /*
             * El día abierto enseña a todos; los demás, tres y un «+N más».
             *
             * Antes el resto vivía sólo en el `title` del «+N más» — invisible en
             * táctil, que es donde más se usa un calendario, y sin manera de
             * llegar con teclado. QA lo reportó como que «no se visualizan las
             * demás personas», y tenían razón: estaban, pero no había forma de
             * verlas.
             */
            const shown = expanded === iso ? occupants : occupants.slice(0, MAX_NAMES);
            const hidden = occupants.length - shown.length;
            const isHoliday = holidays.has(iso);

            return (
              <div
                key={iso}
                className={cx(
                  styles.dayCell,
                  !day.inMonth && styles.dayCellOutside,
                  day.date.isWeekend && styles.dayCellWeekend,
                  isHoliday && styles.dayCellHoliday,
                  day.date.equals(today) && styles.dayCellToday,
                )}
              >
                <div className={styles.dayCellHead}>
                  <span className={styles.dayCellNumber}>{day.date.day}</span>
                  {isHoliday && (
                    <span className={styles.dayCellHolidayTag} title="Día feriado">
                      Feriado
                    </span>
                  )}
                </div>

                <div className={styles.dayCellPeople}>
                  {shown.map((occupant) => (
                    <div
                      key={occupant.key}
                      className={cx(
                        styles.dayPerson,
                        occupant.tentative && styles.dayPersonTentative,
                      )}
                      style={cssVars({ '--person-color': occupant.color })}
                      title={`${occupant.name} · ${occupant.title}`}
                    >
                      <span className={styles.dayPersonDot} />
                      <span className={styles.dayPersonName}>{shortName(occupant.name)}</span>
                    </div>
                  ))}

                  {hidden > 0 && (
                    <button
                      type="button"
                      className={styles.dayMore}
                      aria-expanded={false}
                      onClick={() => {
                        setExpanded(iso);
                      }}
                    >
                      +{hidden} más
                    </button>
                  )}

                  {expanded === iso && occupants.length > MAX_NAMES && (
                    <button
                      type="button"
                      className={styles.dayMore}
                      aria-expanded
                      onClick={() => {
                        setExpanded(null);
                      }}
                    >
                      Ver menos
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
